import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import CookieBanner from '../../components/feature/CookieBanner';

export default function ContactoPage() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    servicio: '',
    localidad: '',
    mensaje: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    document.title = 'Contacto - Vaciado de Pisos en Vic | JUSTCLEAN VIC';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Contacta con JUSTCLEAN VIC para vaciado de pisos y locales en Vic y Osona. Presupuesto sin compromiso. ☎️ 602 36 59 96 | info@vic.justclean.es'
      );
    }

    // Add ContactPage Schema
    const contactPageScript = document.createElement('script');
    contactPageScript.type = 'application/ld+json';
    contactPageScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contacto JUSTCLEAN VIC',
      description: 'Contacta con nosotros para solicitar presupuesto de vaciado de pisos y locales en Vic',
      url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contacto`,
      mainEntity: {
        '@type': 'LocalBusiness',
        name: 'JUSTCLEAN VIC',
        telephone: '+34602365996',
        email: 'info@vic.justclean.es',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Carrer de Jacint Verdaguer, 15',
          addressLocality: 'Vic',
          postalCode: '08500',
          addressRegion: 'Barcelona',
          addressCountry: 'ES',
        },
      },
    });
    document.head.appendChild(contactPageScript);

    return () => {
      document.head.removeChild(contactPageScript);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d51s4lneagc3l4h0tctg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          email: '',
          telefono: '',
          servicio: '',
          localidad: '',
          mensaje: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-secondary/10 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl sm:text-5xl font-bold text-dark mb-6">
                Contacta con Nosotros
              </h1>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                ¿Necesitas <Link to="/servicios#vaciado-pisos" className="text-primary hover:text-red-700 font-semibold">vaciar un piso o local en Vic</Link>? ¿Buscas un servicio especializado en <Link to="/servicios#diogenes" className="text-primary hover:text-red-700 font-semibold">limpieza por síndrome de Diógenes</Link> o <Link to="/servicios#limpieza-extrema" className="text-primary hover:text-red-700 font-semibold">suciedad extrema</Link>? Contacta con JUSTCLEAN VIC y obtén un presupuesto sin compromiso. Atendemos en Vic, Manlleu, Torelló, Taradell y toda la comarca de Osona.
              </p>
            </div>
          </div>
        </section>

        {/* Información de Contacto y Formulario */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Información de Contacto */}
              <div>
                <h2 className="text-3xl font-bold text-dark mb-6">Información de Contacto</h2>
                <p className="text-sm text-gray-700 mb-8 leading-relaxed">
                  Estamos disponibles para atenderte y resolver todas tus dudas sobre nuestros <Link to="/servicios" className="text-primary hover:text-red-700 font-semibold">servicios de vaciado de pisos y locales</Link>, <Link to="/servicios#retirada-muebles" className="text-primary hover:text-red-700 font-semibold">retirada de muebles y electrodomésticos</Link>, limpieza extrema y síndrome de Diógenes en Vic y comarca de Osona. <Link to="/empresa" className="text-primary hover:text-red-700 font-semibold">Conoce más sobre nuestra empresa</Link>.
                </p>

                <div className="space-y-6 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl text-primary"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-dark mb-1">Dirección</h3>
                      <p className="text-sm text-gray-700">
                        Carrer de Jacint Verdaguer, 15<br />
                        08500 Vic, Barcelona
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg flex-shrink-0">
                      <i className="ri-phone-line text-2xl text-primary"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-dark mb-1">Teléfono / WhatsApp</h3>
                      <a href="tel:+34602365996" className="text-sm text-primary hover:text-red-700 font-medium">
                        602 36 59 96
                      </a>
                      <p className="text-xs text-gray-600 mt-1">Disponible 24 horas para urgencias</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-2xl text-primary"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-dark mb-1">Email</h3>
                      <a href="mailto:info@vic.justclean.es" className="text-sm text-primary hover:text-red-700 font-medium">
                        info@vic.justclean.es
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg flex-shrink-0">
                      <i className="ri-time-line text-2xl text-primary"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-dark mb-1">Horario de Atención</h3>
                      <p className="text-sm text-gray-700">
                        Lunes a Viernes: 08:00 - 20:00<br />
                        Sábados: 09:00 - 14:00<br />
                        <span className="text-primary font-medium">Servicio urgente 24h disponible</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Zonas de Servicio */}
                <div className="bg-gradient-to-br from-secondary/10 to-white rounded-lg p-6">
                  <h3 className="text-lg font-bold text-dark mb-3">Zonas de Servicio</h3>
                  <p className="text-sm text-gray-700 mb-3">
                    Ofrecemos nuestros servicios en:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['Vic', 'Manlleu', 'Torelló', 'Taradell', 'Tona', 'Gurb', 'Calldetenes', 'Roda de Ter', 'Folgueroles'].map((zona) => (
                      <span key={zona} className="px-3 py-1.5 bg-white text-dark rounded-full text-xs font-medium border border-gray-200">
                        {zona}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-600 mt-3">
                    ¿Tu zona no aparece? <Link to="/faq" className="text-primary hover:text-red-700 font-semibold">Consulta nuestras preguntas frecuentes</Link> o contáctanos.
                  </p>
                </div>
              </div>

              {/* Formulario de Contacto */}
              <div>
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-dark mb-6">Solicita tu Presupuesto</h2>
                  <form onSubmit={handleSubmit} data-readdy-form id="contacto-form">
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="nombre" className="block text-sm font-semibold text-dark mb-2">
                          Nombre completo *
                        </label>
                        <input
                          type="text"
                          id="nombre"
                          name="nombre"
                          value={formData.nombre}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          placeholder="Tu nombre"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-dark mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          placeholder="tu@email.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="telefono" className="block text-sm font-semibold text-dark mb-2">
                          Teléfono *
                        </label>
                        <input
                          type="tel"
                          id="telefono"
                          name="telefono"
                          value={formData.telefono}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          placeholder="602 36 59 96"
                        />
                      </div>

                      <div>
                        <label htmlFor="servicio" className="block text-sm font-semibold text-dark mb-2">
                          Servicio que necesitas *
                        </label>
                        <select
                          id="servicio"
                          name="servicio"
                          value={formData.servicio}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                        >
                          <option value="">Selecciona un servicio</option>
                          <option value="Vaciado de pisos y casas">Vaciado de pisos y casas</option>
                          <option value="Vaciado de locales comerciales">Vaciado de locales comerciales</option>
                          <option value="Retirada de muebles y electrodomésticos">Retirada de muebles y electrodomésticos</option>
                          <option value="Limpieza síndrome de Diógenes">Limpieza síndrome de Diógenes</option>
                          <option value="Limpieza de suciedad extrema">Limpieza de suciedad extrema</option>
                          <option value="Pintura y pequeñas reparaciones">Pintura y pequeñas reparaciones</option>
                          <option value="Varios servicios">Varios servicios</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="localidad" className="block text-sm font-semibold text-dark mb-2">
                          Localidad *
                        </label>
                        <input
                          type="text"
                          id="localidad"
                          name="localidad"
                          value={formData.localidad}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          placeholder="Vic, Manlleu, Torelló..."
                        />
                      </div>

                      <div>
                        <label htmlFor="mensaje" className="block text-sm font-semibold text-dark mb-2">
                          Mensaje *
                        </label>
                        <textarea
                          id="mensaje"
                          name="mensaje"
                          value={formData.mensaje}
                          onChange={handleChange}
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm resize-none"
                          placeholder="Cuéntanos qué necesitas (máximo 500 caracteres)"
                        ></textarea>
                        <p className="text-xs text-gray-500 mt-1">
                          {formData.mensaje.length}/500 caracteres
                        </p>
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-primary text-white px-8 py-4 rounded-lg font-bold hover:bg-red-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                      >
                        {isSubmitting ? 'Enviando...' : 'Solicitar Presupuesto Sin Compromiso'}
                      </button>

                      {submitStatus === 'success' && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <p className="text-sm text-green-800 font-medium">
                            ¡Mensaje enviado correctamente! Te contactaremos pronto.
                          </p>
                        </div>
                      )}

                      {submitStatus === 'error' && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-sm text-red-800 font-medium">
                            Ha ocurrido un error. Por favor, inténtalo de nuevo o llámanos al 602 36 59 96.
                          </p>
                        </div>
                      )}
                    </div>
                  </form>
                </div>

                <div className="mt-6 flex gap-4">
                  <a
                    href="tel:+34602365996"
                    className="flex-1 bg-dark text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-all duration-300 text-center text-sm whitespace-nowrap"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Llamar Ahora
                  </a>
                  <a
                    href="https://wa.me/34602365996"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-green-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-600 transition-all duration-300 text-center text-sm whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line mr-2"></i>
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mapa */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-dark mb-4">Nuestra Ubicación</h2>
              <p className="text-base text-gray-600 max-w-3xl mx-auto">
                Estamos ubicados en el corazón de Vic, pero ofrecemos nuestros servicios en toda la comarca de Osona y alrededores.
              </p>
            </div>
            <div className="rounded-lg overflow-hidden shadow-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2985.8!2d2.2553!3d41.9304!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDU1JzQ5LjQiTiAywrAxNScxOS4xIkU!5e0!3m2!1ses!2ses!4v1234567890"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación de JUSTCLEAN VIC en Vic, Barcelona"
              ></iframe>
            </div>
          </div>
        </section>

        {/* Por qué elegirnos */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-dark mb-4">
                Por Qué Elegir JUSTCLEAN VIC
              </h2>
              <p className="text-base text-gray-600 max-w-3xl mx-auto">
                Somos la empresa de referencia en <strong>vaciado de pisos y locales en Vic</strong> y comarca de Osona. <Link to="/empresa" className="text-primary hover:text-red-700 font-semibold">Descubre nuestra historia y valores</Link>.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-full mx-auto mb-4">
                  <i className="ri-medal-line text-3xl text-primary"></i>
                </div>
                <h3 className="text-base font-bold text-dark mb-2">+10 Años de Experiencia</h3>
                <p className="text-sm text-gray-600">
                  Más de una década especializados en vaciado y limpieza extrema
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-full mx-auto mb-4">
                  <i className="ri-shield-check-line text-3xl text-primary"></i>
                </div>
                <h3 className="text-base font-bold text-dark mb-2">Servicio Discreto</h3>
                <p className="text-sm text-gray-600">
                  Máxima confidencialidad en todos nuestros trabajos
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-full mx-auto mb-4">
                  <i className="ri-time-line text-3xl text-primary"></i>
                </div>
                <h3 className="text-base font-bold text-dark mb-2">Disponibilidad 24h</h3>
                <p className="text-sm text-gray-600">
                  Servicio urgente disponible cualquier día de la semana
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-full mx-auto mb-4">
                  <i className="ri-hand-heart-line text-3xl text-primary"></i>
                </div>
                <h3 className="text-base font-bold text-dark mb-2">Trato Profesional</h3>
                <p className="text-sm text-gray-600">
                  Equipo cualificado y respetuoso en situaciones delicadas
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
      <CookieBanner />
    </div>
  );
}
